import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Universal Realtime Sync Hook
 * 
 * Creates a reactive data mesh across the entire platform.
 * All components using TanStack Query will automatically re-render
 * when their data dependencies change.
 * 
 * Trigger Map:
 * ┌─────────────────────────┬────────────────────────────────────────────────┐
 * │ Database Change         │ Invalidated Query Keys                         │
 * ├─────────────────────────┼────────────────────────────────────────────────┤
 * │ transcripts.*           │ transcripts, saturation (via transcripts)     │
 * │ codebook_entries.*      │ codebook, codebook-all, excerpt-counts        │
 * │ manual_codes.*          │ all-excerpts, excerpt-counts, codebook-counts │
 * │ ai_codes.*              │ all-excerpts, excerpt-counts                  │
 * │ projects.UPDATE         │ projects, project, transcripts (context shift)│
 * │ memos.*                 │ memos                                         │
 * └─────────────────────────┴────────────────────────────────────────────────┘
 */
export function useRealtimeSync(projectId: string | undefined) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!projectId) return;

    console.log('[useRealtimeSync] Initializing realtime subscriptions for project:', projectId);

    // ═══════════════════════════════════════════════════════════════════════
    // TRANSCRIPTS - Sources, Analysis, Saturation
    // ═══════════════════════════════════════════════════════════════════════
    const transcriptsChannel = supabase
      .channel(`sync-transcripts-${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'transcripts',
          filter: `project_id=eq.${projectId}`,
        },
        (payload) => {
          console.log('[useRealtimeSync] Transcript change:', payload.eventType);
          // Core transcript data
          queryClient.invalidateQueries({ queryKey: ['transcripts', projectId] });
          // Saturation depends on analyzed transcripts
          queryClient.invalidateQueries({ queryKey: ['saturation', projectId] });
          // Excerpt counts may change with new analysis
          queryClient.invalidateQueries({ queryKey: ['excerpt-counts', projectId] });
          queryClient.invalidateQueries({ queryKey: ['all-excerpts', projectId] });
        }
      )
      .subscribe();

    // ═══════════════════════════════════════════════════════════════════════
    // CODEBOOK ENTRIES - Codes, Themes, Buckets
    // ═══════════════════════════════════════════════════════════════════════
    const codebookChannel = supabase
      .channel(`sync-codebook-${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'codebook_entries',
          filter: `project_id=eq.${projectId}`,
        },
        (payload) => {
          console.log('[useRealtimeSync] Codebook change:', payload.eventType);
          // All codebook queries
          queryClient.invalidateQueries({ queryKey: ['codebook', projectId] });
          queryClient.invalidateQueries({ queryKey: ['codebook-all', projectId] });
          // Excerpt counts reference codebook entries
          queryClient.invalidateQueries({ queryKey: ['excerpt-counts', projectId] });
          queryClient.invalidateQueries({ queryKey: ['codebook-counts', projectId] });
          // Findings synthesis uses codebook data
          queryClient.invalidateQueries({ queryKey: ['transcripts', projectId] });
        }
      )
      .subscribe();

    // ═══════════════════════════════════════════════════════════════════════
    // MANUAL CODES - User-applied code instances
    // ═══════════════════════════════════════════════════════════════════════
    const manualCodesChannel = supabase
      .channel(`sync-manual-codes-${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'manual_codes',
        },
        (payload) => {
          console.log('[useRealtimeSync] Manual code change:', payload.eventType);
          queryClient.invalidateQueries({ queryKey: ['manual-codes'] });
          queryClient.invalidateQueries({ queryKey: ['all-excerpts', projectId] });
          queryClient.invalidateQueries({ queryKey: ['excerpt-counts', projectId] });
          queryClient.invalidateQueries({ queryKey: ['codebook-counts', projectId] });
        }
      )
      .subscribe();

    // ═══════════════════════════════════════════════════════════════════════
    // AI CODES - AI-generated code instances from analysis
    // ═══════════════════════════════════════════════════════════════════════
    const aiCodesChannel = supabase
      .channel(`sync-ai-codes-${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ai_codes',
        },
        (payload) => {
          console.log('[useRealtimeSync] AI code change:', payload.eventType);
          queryClient.invalidateQueries({ queryKey: ['all-excerpts', projectId] });
          queryClient.invalidateQueries({ queryKey: ['excerpt-counts', projectId] });
        }
      )
      .subscribe();

    // ═══════════════════════════════════════════════════════════════════════
    // PROJECT - Research Lens, Focus, Methodology
    // ═══════════════════════════════════════════════════════════════════════
    const projectChannel = supabase
      .channel(`sync-project-${projectId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'projects',
          filter: `id=eq.${projectId}`,
        },
        (payload) => {
          console.log('[useRealtimeSync] Project change:', payload.eventType);
          // Project data
          // IMPORTANT: different parts of the app use different query keys for a single project
          // (e.g. ProjectView uses ['project', slug], other places may use ['project', projectId]).
          // To guarantee Focus edits propagate everywhere, invalidate all project-related queries.
          queryClient.invalidateQueries({ queryKey: ['projects'] });
          queryClient.invalidateQueries({
            predicate: (q) => {
              const key = q.queryKey;
              return Array.isArray(key) && (key[0] === 'project' || key[0] === 'projects');
            },
          });
          // When research lens changes, findings context shifts
          queryClient.invalidateQueries({ queryKey: ['transcripts', projectId] });
          // Saturation calculation uses methodology
          queryClient.invalidateQueries({ queryKey: ['saturation', projectId] });
        }
      )
      .subscribe();

    // ═══════════════════════════════════════════════════════════════════════
    // MEMOS - Researcher notes and annotations
    // ═══════════════════════════════════════════════════════════════════════
    const memosChannel = supabase
      .channel(`sync-memos-${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'memos',
          filter: `project_id=eq.${projectId}`,
        },
        (payload) => {
          console.log('[useRealtimeSync] Memo change:', payload.eventType);
          queryClient.invalidateQueries({ queryKey: ['memos', projectId] });
        }
      )
      .subscribe();

    // ═══════════════════════════════════════════════════════════════════════
    // SOURCE CATEGORIES - Grouping for transcripts
    // ═══════════════════════════════════════════════════════════════════════
    const categoriesChannel = supabase
      .channel(`sync-categories-${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'source_categories',
          filter: `project_id=eq.${projectId}`,
        },
        (payload) => {
          console.log('[useRealtimeSync] Category change:', payload.eventType);
          queryClient.invalidateQueries({ queryKey: ['source-categories', projectId] });
        }
      )
      .subscribe();

    // ═══════════════════════════════════════════════════════════════════════
    // AUDIO RECORDINGS - Linked audio files
    // ═══════════════════════════════════════════════════════════════════════
    const audioChannel = supabase
      .channel(`sync-audio-${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'audio_recordings',
          filter: `project_id=eq.${projectId}`,
        },
        (payload) => {
          console.log('[useRealtimeSync] Audio recording change:', payload.eventType);
          queryClient.invalidateQueries({ queryKey: ['audio-recordings', projectId] });
        }
      )
      .subscribe();

    // Cleanup all subscriptions
    return () => {
      console.log('[useRealtimeSync] Cleaning up subscriptions for project:', projectId);
      supabase.removeChannel(transcriptsChannel);
      supabase.removeChannel(codebookChannel);
      supabase.removeChannel(manualCodesChannel);
      supabase.removeChannel(aiCodesChannel);
      supabase.removeChannel(projectChannel);
      supabase.removeChannel(memosChannel);
      supabase.removeChannel(categoriesChannel);
      supabase.removeChannel(audioChannel);
    };
  }, [projectId, queryClient]);
}

/**
 * Hook specifically for project-level sync (used in contexts/providers)
 * This is a lighter version that doesn't need a projectId filter
 */
export function useGlobalProjectSync() {
  const queryClient = useQueryClient();

  useEffect(() => {
    // Only subscribe when there is an authenticated session to avoid
    // WebSocket connection errors on public pages (landing, pricing, etc.)
    let cancelled = false;

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (cancelled || !session) return;

      const projectsChannel = supabase
        .channel('sync-all-projects')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'projects',
          },
          (payload) => {
            console.log('[useGlobalProjectSync] Projects change:', payload.eventType);
            queryClient.invalidateQueries({ queryKey: ['projects'] });
          }
        )
        .subscribe();

      // Store channel ref for cleanup
      cleanupRef = () => {
        supabase.removeChannel(projectsChannel);
      };
    });

    let cleanupRef: (() => void) | null = null;

    return () => {
      cancelled = true;
      cleanupRef?.();
    };
  }, [queryClient]);
}
